/*
 * File: RepetitionStep.js
 * Version: v1.0.0
 * Last Modified: 2025-01-27
 * Project: Asterism User GUI
 * Description: Form step component for configuring repetitive observations
 * Dependencies: React, Material-UI, React Hook Form, date-fns
 * Change Log:
 *   v1.0.0 - Initial implementation for repetitive observation support
 */

import React, { useEffect, useRef } from 'react';
import {
  Box,
  FormControlLabel,
  TextField,
  Typography,
  Grid,
  Switch,
  Alert,
  Paper,
  Chip,
  Divider,
  InputAdornment,
  Tooltip,
  IconButton
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFormContext, Controller } from 'react-hook-form';
import { addDays } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RepeatIcon from '@mui/icons-material/Repeat';

const RepetitionStep = () => {
  const { control, watch, setValue } = useFormContext();
  const wasRepetitive = useRef(false);

  const isRepetitive = watch('isRepetitive');
  const startDate = watch('repetitionStartDate');
  const endDate = watch('repetitionEndDate');
  const intervalDays = watch('repetitionIntervalDays');
  const intervalHours = watch('repetitionIntervalHours');
  const maxExecutions = watch('repetitionMaxExecutions');
  const windowHours = watch('repetitionWindowHours');
  const priorityBoostPerDay = watch('repetitionPriorityBoostPerDay');

  // Initialize default values when repetition is FIRST enabled (toggle from off to on)
  useEffect(() => {
    if (isRepetitive && !wasRepetitive.current) {
      // Only set if undefined (first time enabling)
      if (intervalDays === undefined) setValue('repetitionIntervalDays', 0);
      if (intervalHours === undefined) setValue('repetitionIntervalHours', 0);
      if (windowHours === undefined) setValue('repetitionWindowHours', 2);
      if (priorityBoostPerDay === undefined) setValue('repetitionPriorityBoostPerDay', 0);
      wasRepetitive.current = true;
    } else if (!isRepetitive) {
      wasRepetitive.current = false;
    }
  }, [isRepetitive, intervalDays, intervalHours, windowHours, priorityBoostPerDay, setValue]);

  // Calculate estimated executions
  const calculateExecutions = () => {
    if (!isRepetitive || !startDate) return 0;

    const days = intervalDays || 0;
    const hours = intervalHours || 0;
    const intervalMs = (days * 24 + hours) * 3600000;
    if (intervalMs === 0) return 0;

    let durationMs;
    if (endDate) {
      durationMs = new Date(endDate).getTime() - new Date(startDate).getTime();
    } else {
      // Default to 1 year if no end date
      durationMs = 365 * 86400000;
    }

    const estimated = Math.floor(durationMs / intervalMs) + 1;

    // Apply max executions limit if set
    if (maxExecutions && maxExecutions > 0) {
      return Math.min(estimated, maxExecutions);
    }

    return Math.min(estimated, 1000); // Cap at 1000 for display
  };

  const estimatedExecutions = calculateExecutions();

  // Helper to set preset intervals
  const setPresetInterval = (days, hours = 0) => {
    setValue('repetitionIntervalDays', days);
    setValue('repetitionIntervalHours', hours);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        <RepeatIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
        Repetition Configuration
      </Typography>

      <Typography variant="body2" color="textSecondary" paragraph>
        Configure your observation to repeat periodically over time. Perfect for monitoring
        variable stars, tracking asteroids, or capturing seasonal changes.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Controller
          name="isRepetitive"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    // If turning off repetitive, clear the interval values to avoid confusion
                    if (!e.target.checked) {
                      setValue('repetitionIntervalDays', 0);
                      setValue('repetitionIntervalHours', 0);
                      setValue('repetitionStartDate', null);
                      setValue('repetitionEndDate', null);
                      setValue('repetitionMaxExecutions', null);
                      setValue('repetitionWindowHours', 2);
                      setValue('repetitionSkipIfDelayedHours', null);
                      setValue('repetitionPriorityBoostPerDay', 0);
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" fontWeight="medium">
                  Enable Repetitive Observation
                </Typography>
              }
            />
          )}
        />
      </Box>

      {isRepetitive && (
        <>
          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <Grid container spacing={3}>
              {/* Observation Period */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  <ScheduleIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'bottom' }} />
                  Observation Period
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="repetitionStartDate"
                    control={control}
                    defaultValue={null}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
                      // Convert stored UTC date back to local for display
                      let displayValue = null;
                      if (value) {
                        const utcDate = new Date(value);
                        displayValue = new Date(
                          utcDate.getUTCFullYear(),
                          utcDate.getUTCMonth(),
                          utcDate.getUTCDate(),
                          utcDate.getUTCHours(),
                          utcDate.getUTCMinutes(),
                          0, 0
                        );
                      }

                      return (
                        <DateTimePicker
                          {...field}
                          value={displayValue}
                          onChange={(newValue) => {
                            if (newValue) {
                              const year = newValue.getFullYear();
                              const month = newValue.getMonth();
                              const date = newValue.getDate();
                              const hours = newValue.getHours();
                              const minutes = newValue.getMinutes();
                              const utcDate = new Date(Date.UTC(year, month, date, hours, minutes, 0, 0));
                              onChange(utcDate);
                            } else {
                              onChange(null);
                            }
                          }}
                          label="Start Date & Time (UTC, Optional)"
                          format="yyyy-MM-dd HH:mm"
                          ampm={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!error,
                              helperText: error?.message || 'UTC time to start the series (defaults to now if not specified)'
                            }
                          }}
                          minDateTime={new Date()}
                        />
                      );
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="repetitionEndDate"
                    control={control}
                    defaultValue={null}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => {
                      // Convert stored UTC date back to local for display
                      let displayValue = null;
                      if (value) {
                        const utcDate = new Date(value);
                        displayValue = new Date(
                          utcDate.getUTCFullYear(),
                          utcDate.getUTCMonth(),
                          utcDate.getUTCDate(),
                          utcDate.getUTCHours(),
                          utcDate.getUTCMinutes(),
                          0, 0
                        );
                      }

                      return (
                        <DateTimePicker
                          {...field}
                          value={displayValue}
                          onChange={(newValue) => {
                            if (newValue) {
                              const year = newValue.getFullYear();
                              const month = newValue.getMonth();
                              const date = newValue.getDate();
                              const hours = newValue.getHours();
                              const minutes = newValue.getMinutes();
                              const utcDate = new Date(Date.UTC(year, month, date, hours, minutes, 0, 0));
                              onChange(utcDate);
                            } else {
                              onChange(null);
                            }
                          }}
                          label="End Date & Time (UTC, Optional)"
                          format="yyyy-MM-dd HH:mm"
                          ampm={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!error,
                              helperText: 'UTC time - leave empty for ongoing monitoring'
                            }
                          }}
                          minDateTime={startDate ? addDays(new Date(startDate), 1) : new Date()}
                        />
                      );
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Repetition Interval */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
                  Repetition Interval
                </Typography>

                {/* Quick presets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Quick presets:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="Every 4 hours" onClick={() => setPresetInterval(0, 4)} size="small" />
                    <Chip label="Twice daily" onClick={() => setPresetInterval(0, 12)} size="small" />
                    <Chip label="Daily" onClick={() => setPresetInterval(1, 0)} size="small" />
                    <Chip label="Every 3 days" onClick={() => setPresetInterval(3, 0)} size="small" />
                    <Chip label="Weekly" onClick={() => setPresetInterval(7, 0)} size="small" />
                    <Chip label="Bi-weekly" onClick={() => setPresetInterval(14, 0)} size="small" />
                    <Chip label="Monthly" onClick={() => setPresetInterval(30, 0)} size="small" />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="repetitionIntervalDays"
                  control={control}
                  defaultValue={0}
                  rules={{
                    min: { value: 0, message: 'Must be 0 or more days' },
                    validate: (value) => {
                      const hours = watch('repetitionIntervalHours') || 0;
                      const totalHours = (parseFloat(value) || 0) * 24 + (parseFloat(hours) || 0);
                      if (totalHours < 0.1) {
                        return 'Minimum interval is 0.1 hours (6 minutes)';
                      }
                      return true;
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Days"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">days</InputAdornment>,
                        inputProps: { min: 0, step: 1 }
                      }}
                      onChange={(e) => {
                        // Allow empty value or convert to number
                        const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        field.onChange(val);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Controller
                  name="repetitionIntervalHours"
                  control={control}
                  defaultValue={0}
                  rules={{
                    min: { value: 0, message: 'Must be 0 or more hours' },
                    max: { value: 23.9, message: 'Must be less than 24 hours' },
                    validate: (value) => {
                      const days = watch('repetitionIntervalDays') || 0;
                      const totalHours = (parseFloat(days) || 0) * 24 + (parseFloat(value) || 0);
                      if (totalHours < 0.1) {
                        return 'Minimum interval is 0.1 hours (6 minutes)';
                      }
                      return true;
                    }
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Hours"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                        inputProps: { min: 0, max: 23.9, step: 0.1 }
                      }}
                      onChange={(e) => {
                        // Allow empty value or convert to number
                        const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                        field.onChange(val);
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Execution Limits */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
                  Execution Limits
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="repetitionMaxExecutions"
                  control={control}
                  defaultValue={null}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={field.value || ''}
                      type="number"
                      label="Maximum Executions (Optional)"
                      fullWidth
                      error={!!error}
                      helperText="Leave empty for unlimited (within date range)"
                      InputProps={{
                        inputProps: { min: 1, max: 1000 }
                      }}
                      onChange={(e) => {
                        const val = e.target.value === '' ? null : parseInt(e.target.value);
                        field.onChange(val);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{
                  p: 2,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: 1,
                  textAlign: 'center'
                }}>
                  <Typography variant="h4" fontWeight="bold">
                    {estimatedExecutions}
                  </Typography>
                  <Typography variant="body2">
                    Estimated Executions
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Advanced Settings */}
          <Paper elevation={1} sx={{ p: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Advanced Settings
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="repetitionWindowHours"
                  control={control}
                  defaultValue={2}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Execution Window"
                      fullWidth
                      error={!!error}
                      helperText="Total width in hours, centered on the ideal time (e.g. 2 = ±1h)"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="The total time window around each scheduled execution when the observation can run, split evenly before and after the ideal time (e.g. 6 hours = ideal time ±3 hours)">
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                        inputProps: { min: 0.5, max: 12, step: 0.5 }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="repetitionSkipIfDelayedHours"
                  control={control}
                  defaultValue={null}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Skip If Delayed (Optional)"
                      fullWidth
                      error={!!error}
                      helperText="Skip execution if delayed by this many hours"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Automatically skip an execution if it cannot start within this many hours of the ideal time">
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                        inputProps: { min: 1, max: 48, step: 1 }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name="repetitionPriorityBoostPerDay"
                  control={control}
                  defaultValue={0}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Priority Boost Per Day"
                      fullWidth
                      error={!!error}
                      helperText="Increase priority when delayed"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Automatically increase priority by this amount for each day the observation is delayed">
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                        inputProps: { min: 0, max: 5, step: 0.5 }
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Info Alert */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>How Repetitive Observations Work:</strong>
            </Typography>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Your observation will automatically repeat at the specified interval</li>
              <li>Each execution gets its own tracking and can complete independently</li>
              <li>If an execution is delayed, priority can automatically increase</li>
              <li>You can pause or stop the series at any time from the observation details</li>
              <li>All execution history is preserved for your analysis</li>
            </ul>
          </Alert>
        </>
      )}
    </Box>
  );
};

export default RepetitionStep;